import React, { useState, useEffect, useMemo, useCallback } from 'react';
import {
  Box,
  Typography,
  Paper,
  Button,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  IconButton,
  Tooltip,
  Chip,
  TextField,
  InputAdornment,
  Select,
  MenuItem,
  useTheme,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Search as SearchIcon,
  RateReview as ReviewIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { debounce } from 'lodash';
import grapeService, { Grape } from '../../../services/grapeService';
import ImportExportModal from '../../../components/ImportExportModal';
import { grapeConfig } from '../../../utils/importExportConfigs';

const GrapeList: React.FC = () => {
  const navigate = useNavigate();
  const theme = useTheme();

  const [loading, setLoading] = useState<boolean>(true);
  const [grapes, setGrapes] = useState<Grape[]>([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [reviewFilter, setReviewFilter] = useState<'all' | 'needs_review' | 'complete'>('all');
  const [importExportOpen, setImportExportOpen] = useState(false);

  const [pagination, setPagination] = useState({
    page: 0, // MUI uses 0-indexed pages
    pageSize: 20,
    totalItems: 0,
    totalPages: 0
  });

  const debouncedSearch = useMemo(
    () => debounce((q: string) => setSearchQuery(q), 300),
    []
  );

  // Function to fetch grapes based on current state
  const fetchGrapes = useCallback(async () => {
    setLoading(true);
    try {
      const needsReview = reviewFilter === 'needs_review' ? true : reviewFilter === 'complete' ? false : undefined;
      // Get all grapes
      const response = await grapeService.getAllGrapes(
        pagination.page + 1, // API uses 1-indexed pages
        pagination.pageSize,
        'primaryName',
        'asc',
        searchQuery,
        needsReview
      );
      
      setGrapes(response.data);
      setPagination({
        ...pagination,
        totalItems: response.pagination.totalItems,
        totalPages: response.pagination.totalPages
      });
    } catch (error) {
      console.error('Failed to fetch grapes:', error);
    } finally {
      setLoading(false);
    }
  }, [pagination.page, pagination.pageSize, searchQuery, reviewFilter]);

  // Load grapes when component mounts or when params change
  useEffect(() => {
    fetchGrapes();
  }, [fetchGrapes]);

  // Handle page change
  const handleChangePage = (_: unknown, newPage: number) => {
    setPagination({ ...pagination, page: newPage });
  };

  // Handle rows per page change
  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setPagination({
      ...pagination,
      pageSize: parseInt(event.target.value, 10),
      page: 0 // Reset to first page
    });
  };

  // Handle accept (resolve needsReview)
  const handleAcceptGrape = async (event: React.MouseEvent, grape: Grape) => {
    event.stopPropagation();
    try {
      await grapeService.updateGrape(grape._id, { needsReview: false });
      setGrapes(prev => prev.map(g => g._id === grape._id ? { ...g, needsReview: false } : g));
    } catch {
      alert('Failed to accept. Please try again.');
    }
  };

  // Handle edit grape
  const handleEditGrape = (event: React.MouseEvent, grape: Grape) => {
    event.stopPropagation();
    navigate(`/admin/grapes/edit/${grape._id}`);
  };

  // Handle delete grape
  const handleDeleteGrape = async (event: React.MouseEvent, grape: Grape) => {
    event.stopPropagation();
    if (window.confirm(`Are you sure you want to delete the grape "${grape.primaryName}"?`)) {
      try {
        await grapeService.deleteGrape(grape._id);
        // Refresh the list
        fetchGrapes();
      } catch (error) {
        console.error('Failed to delete grape:', error);
        alert('Failed to delete grape. Please try again.');
      }
    }
  };

  // Get color display name
  const getColorDisplayName = (color: string): string => {
    switch (color) {
      case 'RED': return 'Red';
      case 'WHITE': return 'White';
      case 'PINK_ROSE': return 'Rosé';
      default: return color;
    }
  };

  // Get color chip based on grape color
  const getColorChip = (color: string) => {
    let chipColor = '';
    let textColor = '';
    
    switch (color) {
      case 'RED':
        chipColor = '#8B0000';
        textColor = 'white';
        break;
      case 'WHITE':
        chipColor = '#F0E68C';
        textColor = 'black';
        break;
      case 'PINK_ROSE':
        chipColor = '#FFB6C1';
        textColor = 'black';
        break;
      default:
        chipColor = 'default';
    }
    
    return (
      <Chip 
        label={getColorDisplayName(color)} 
        size="small" 
        sx={{ 
          backgroundColor: chipColor, 
          color: textColor,
          fontWeight: 'medium'
        }} 
      />
    );
  };

  return (
    <Box sx={{ p: 3 }}>
      <Paper
        elevation={0}
        sx={{
          p: 3,
          mb: 3,
          borderRadius: 2,
          backdropFilter: 'blur(10px)',
          border: '1px solid rgba(255, 255, 255, 0.1)',
          boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
        }}
      >
        <Box sx={{
          mb: 2,
          display: 'flex',
          gap: 2,
          flexDirection: { xs: 'column', sm: 'row' },
          alignItems: { xs: 'stretch', sm: 'center' },
          flexWrap: 'wrap',
        }}>
          <TextField
            placeholder="Search grapes..."
            variant="outlined"
            size="small"
            onChange={(e) => debouncedSearch(e.target.value)}
            sx={{
              flexGrow: 1,
              maxWidth: { xs: '100%', sm: 320 },
              '& .MuiOutlinedInput-root': {
                backgroundColor: theme.palette.background.paper,
              },
            }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={{ color: theme.palette.text.secondary }} />
                </InputAdornment>
              ),
            }}
          />

          <Select
            value={reviewFilter}
            onChange={(e) => setReviewFilter(e.target.value as typeof reviewFilter)}
            sx={{ minWidth: 130, '& .MuiSelect-select': { py: '6.5px', fontSize: '0.875rem' } }}
          >
            <MenuItem value="all">All</MenuItem>
            <MenuItem value="needs_review">Needs Review</MenuItem>
            <MenuItem value="complete">Complete</MenuItem>
          </Select>

          <Button
            variant="outlined"
            onClick={() => setImportExportOpen(true)}
          >
            Import / Export
          </Button>
          <Button
            variant="contained"
            color="primary"
            startIcon={<AddIcon />}
            onClick={() => navigate('/admin/grapes/create')}
          >
            Add Grape
          </Button>
        </Box>
        
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
            <CircularProgress />
          </Box>
        ) : grapes.length === 0 ? (
          <Typography variant="body1" align="center" sx={{ p: 3 }}>
            No grapes found. Click "Add Grape" to create one.
          </Typography>
        ) : (
          <>
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Primary Name</TableCell>
                    <TableCell>Color</TableCell>
                    <TableCell>Aliases</TableCell>
                    <TableCell>Species</TableCell>
                    <TableCell>Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {grapes.map((grape) => (
                    <TableRow 
                      key={grape._id}
                      hover
                      onClick={() => navigate(`/admin/grapes/edit/${grape._id}`)}
                      sx={{ cursor: 'pointer' }}
                    >
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Typography variant="body2">{grape.primaryName}</Typography>
                          {grape.needsReview && (
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                              <Tooltip title="Needs review">
                                <ReviewIcon fontSize="small" sx={{ color: theme.palette.warning.main }} />
                              </Tooltip>
                              <Chip
                                label="Accept"
                                size="small"
                                color="success"
                                variant="outlined"
                                onClick={(e) => handleAcceptGrape(e, grape)}
                                sx={{ cursor: 'pointer', height: 20, fontSize: '0.7rem' }}
                              />
                            </Box>
                          )}
                        </Box>
                      </TableCell>
                      <TableCell>{getColorChip(grape.color)}</TableCell>
                      <TableCell>
                        {grape.aliases.length > 0 ? (
                          <Typography variant="body2">
                            {grape.aliases.slice(0, 2).map(alias => alias.name).join(', ')}
                            {grape.aliases.length > 2 && ` +${grape.aliases.length - 2} more`}
                          </Typography>
                        ) : (
                          '-'
                        )}
                      </TableCell>
                      <TableCell>{grape.species || '-'}</TableCell>
                      <TableCell>
                        <Tooltip title="Edit">
                          <IconButton 
                            size="small"
                            onClick={(e) => handleEditGrape(e, grape)}
                            color="primary"
                          >
                            <EditIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        
                        <Tooltip title="Delete">
                          <IconButton 
                            size="small" 
                            color="error"
                            onClick={(e) => handleDeleteGrape(e, grape)}
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
            <TablePagination
              rowsPerPageOptions={[10, 20, 50]}
              component="div"
              count={pagination.totalItems}
              rowsPerPage={pagination.pageSize}
              page={pagination.page}
              onPageChange={handleChangePage}
              onRowsPerPageChange={handleChangeRowsPerPage}
            />
          </>
        )}
      </Paper>
      <ImportExportModal
        open={importExportOpen}
        onClose={() => { setImportExportOpen(false); fetchGrapes(); }}
        config={grapeConfig}
      />
    </Box>
  );
};

export default GrapeList;
